import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Sparkles, Heart } from "lucide-react";
import { api, CATEGORIES } from "../lib/api";
import ShopMap from "../components/ShopMap";

const HERO_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/3ddse8nt_TTTsukra-1-1024x576-1024x576.png";
const ELEONORE_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/3igewykf_robe%20grande%20taille%20fusha.webp";

// User's own photos - dynamic gallery
const GALLERY = [
  "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/imyml1qj_%23publicit%C3%A9%20On%20ma%20souvent%20dit%20d%C3%A9viter%20les%20rayures.%20Heureusement%2C%20je%20nai%20jamais%20%C3%A9t%C3%A9%20tr%C3%A8s%20dou%C3%A9e.webp",
  "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/hkhva2ls_%23publicit%C3%A9%20On%20passe%20parfois%20une%20vie%20%C3%A0%20vouloir%20se%20faire%20discr%C3%A8te%E2%80%A6%20jusquau%20jour%20o%C3%B9%20lon%20d%C3%A9cide%20d.webp",
  "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/xekybhfp_Le%20plus%20difficile%20%20Choisir.%20%F0%9F%92%99%F0%9F%92%9AMais%20qui%20a%20dit%20quil%20fallait%20toujours%20choisir%20une%20seule%20couleur.webp",
  "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/3vxgd2gi_%F0%9F%93%B8%C2%A0%20Une%20tenue%20dans%20laquelle%20je%20me%20sens%20moi%20%E2%9D%A4%EF%B8%8FIl%20suffit%20parfois%20d%E2%80%99une%20robe%20bien%20coup%C3%A9e%20et%20d%E2%80%99un%20p.webp",
  "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/m0uhrrdc_Un%20petit%20voyage%20%C3%A0%20travers%20un%20petit%20look%20britannique%20%F0%9F%98%8DPhotographe%20%40travisportraits%20%F0%9F%98%98%23plussize%20.webp",
];

const CATEGORY_META = {
  women:       { headline: "La mode femme, version grande taille", img: "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/1z62rfi7_ChatGPT%20Image%2022%20juil.%202026%2C%2023_56_29.png" },
  men:         { headline: "La mode homme plus size", img: "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/x2cwy2f3_ChatGPT%20Image%2022%20juil.%202026%2C%2023_56_23.png" },
  lingerie:    { headline: "Belle et ronde en lingerie", img: "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/7289j8j4_ChatGPT%20Image%2022%20juil.%202026%2C%2023_56_17.png" },
  sports:      { headline: "Sportive et heureuse avec ses rondeurs", img: "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/sjuazpbh_ChatGPT%20Image%2022%20juil.%202026%2C%2023_56_09.png" },
  furniture:   { headline: "Du mobilier pour notre confort", img: "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/9o8owm3g_ChatGPT%20Image%2022%20juil.%202026%2C%2023_56_01.png" },
  associations:{ headline: "Services et associations", img: "https://customer-assets-4nw71qhi.emergentagent.net/job_plus-size-directory/artifacts/pv8rs48n_ChatGPT%20Image%2022%20juil.%202026%2C%2023_56_37.png" },
};

const MARQUEE_ITEMS = [
  "Style ronde & assumée",
  "boutiques référencées",
  "6 univers",
  "Toute la France",
  "100% Curve",
];

export default function Home() {
  const [shops, setShops] = useState([]);
  const [active, setActive] = useState(CATEGORIES.map((c) => c.key));
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/shops").then((r) => setShops(r.data));
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3)));
  }, []);

  const toggle = (k) => setActive((a) => a.includes(k) ? a.filter((x) => x !== k) : [...a, k]);

  const marqueeItems = useMemo(() => {
    const items = [
      MARQUEE_ITEMS[0],
      `${shops.length} ${MARQUEE_ITEMS[1]}`,
      MARQUEE_ITEMS[2],
      MARQUEE_ITEMS[3],
      MARQUEE_ITEMS[4],
    ];
    return [...items, ...items, ...items, ...items];
  }, [shops.length]);

  // Duplicate gallery for continuous marquee scroll
  const galleryLoop = useMemo(() => [...GALLERY, ...GALLERY, ...GALLERY], []);

  return (
    <main data-testid="home-page" className="bg-white">
      {/* ======================= HERO — full-bleed binoculars ======================= */}
      <section className="relative overflow-hidden bg-white">
        {/* Yellow dots decoration */}
        <div className="absolute top-40 left-8 w-52 h-40 yellow-dots-bg hidden md:block" />
        <div className="absolute bottom-20 right-20 w-40 h-32 yellow-dots-bg hidden md:block" />

        {/* HUGE mustard blob background */}
        <div className="absolute right-[-8%] top-[3%] w-[72%] h-[96%] rounded-full bg-[var(--agt-mustard)] hidden md:block" />
        <div className="absolute right-[-6%] top-[5%] w-[68%] h-[92%] rounded-full bg-[var(--agt-mustard-soft)] opacity-40 hidden md:block" />

        {/* Full-height binoculars image */}
        <img
          src={HERO_IMG}
          alt="À la recherche de ta boutique grande taille"
          className="absolute right-[-2%] top-0 h-[105%] w-auto max-w-[72%] object-contain object-right-bottom hidden md:block z-[2]"
          style={{ filter: "drop-shadow(0 25px 40px rgba(36,53,89,0.25))" }}
        />

        <div className="relative max-w-7xl mx-auto px-6 md:px-8 pt-14 md:pt-20 pb-16 md:pb-24 z-[3]">
          {/* Mobile hero image */}
          <div className="md:hidden relative w-full max-w-md mx-auto mb-8">
            <div className="absolute inset-0 bg-[var(--agt-mustard)] rounded-full" />
            <img src={HERO_IMG} alt="Recherche boutique" className="relative w-full h-auto" />
          </div>

          <div className="max-w-[62%] md:max-w-[50%]" style={{ maxWidth: "min(620px, 55%)" }}>
            <span className="eyebrow">Annuaire Grande Taille</span>
            <h1 className="font-heading mt-6 text-[var(--agt-navy)]" style={{ fontSize: "clamp(2.6rem, 6.5vw, 5.8rem)" }}>
              Trouve <span className="text-[var(--agt-mustard-deep)]">ta&nbsp;boutique</span> grande&nbsp;taille,
              <br/><span className="font-heading-accent">où que tu sois.</span>
            </h1>
            <p className="mt-8 text-lg text-[var(--agt-ink-muted)] max-w-xl leading-relaxed">
              <strong className="text-[var(--agt-navy)]">100 % digital et 100 % curve.</strong> L'annuaire cartographique de toutes les marques et associations grande taille en France.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/carte" data-testid="hero-cta-map" className="btn-primary">
                <MapPin className="w-5 h-5"/> Explorer la carte
              </Link>
              <Link to="/abonnement" data-testid="hero-cta-subscribe" className="btn-ghost">
                <Sparkles className="w-5 h-5"/> Référencer ma boutique
              </Link>
            </div>
            <div className="mt-10 inline-flex items-center gap-2 bg-white px-5 py-3 rounded-full shadow-2xl border border-[var(--agt-line)]">
              <Heart className="w-4 h-4 text-[var(--agt-mustard-deep)] fill-[var(--agt-mustard)]"/>
              <span className="font-bold text-sm text-[var(--agt-navy)]">{shops.length} boutiques référencées</span>
            </div>
          </div>
        </div>
      </section>

      {/* ======================= MARQUEE right→left ======================= */}
      <section className="border-y border-[var(--agt-navy)] bg-[var(--agt-navy)] text-white overflow-hidden py-4">
        <div className="marquee-wrapper">
          <div className="marquee-track font-bold text-lg" aria-hidden="true">
            {marqueeItems.map((t, i) => (
              <span key={i} className="flex items-center gap-8 whitespace-nowrap">
                <span className="text-[var(--agt-mustard)] text-xl">★</span>
                <span>{t}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ======================= MAP PREVIEW ======================= */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
          <div>
            <span className="eyebrow">La grande carte</span>
            <h2 className="font-heading mt-4 text-[var(--agt-navy)]" style={{ fontSize: "clamp(2.2rem, 5vw, 3.8rem)" }}>
              Toutes les boutiques,<br/><span className="font-heading-accent">en un coup d'œil.</span>
            </h2>
          </div>
          <Link to="/carte" className="btn-ghost self-start" data-testid="home-see-map">
            Voir en grand
          </Link>
        </div>
        <div className="rounded-3xl overflow-hidden shadow-2xl">
          <ShopMap shops={shops} active={active} onToggle={toggle} height="62vh"/>
        </div>
      </section>

      {/* ======================= 6 UNIVERSES — bubbles with image bg + text on top ======================= */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-28">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="eyebrow">Nos 6 univers</span>
          <h2 className="font-heading mt-4 text-[var(--agt-navy)]" style={{ fontSize: "clamp(2.2rem, 5vw, 3.8rem)" }}>
            Trouver la bonne boutique était<br/><span className="font-heading-accent">un parcours du combattant.</span>
          </h2>
          <p className="mt-6 text-[var(--agt-ink-muted)] text-lg">Plus maintenant.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10 max-w-5xl mx-auto">
          {CATEGORIES.map((c, i) => {
            const meta = CATEGORY_META[c.key];
            return (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, scale: 0.7 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, type: "spring", stiffness: 90 }}
                data-testid={`category-card-${c.key}`}
              >
                <Link to={`/carte?cat=${c.key}`} className="block group">
                  <div
                    className="bubble relative"
                    style={meta.img ? {
                      backgroundImage: `url(${meta.img})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    } : {
                      background: c.color,
                    }}
                  >
                    {meta.img && (
                      <>
                        <div
                          className="absolute inset-0 rounded-full transition-opacity duration-300 group-hover:opacity-25"
                          style={{ background: c.color, opacity: 0.45, mixBlendMode: "multiply" }}
                        />
                        <div className="absolute inset-0 rounded-full bg-gradient-to-t from-black/45 via-transparent to-transparent"/>
                      </>
                    )}
                    <h3
                      className="relative font-heading text-2xl md:text-3xl leading-tight drop-shadow-lg z-10 px-4"
                      style={{ color: (!meta.img && c.key === "sports") ? "#243559" : "white" }}
                    >
                      {meta.headline}
                    </h3>
                  </div>
                  <div className="text-center mt-4 font-bold text-sm uppercase tracking-widest" style={{ color: c.color }}>
                    {c.label} ›
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ======================= TRANSITION GALLERY — animated photo strip ======================= */}
      <section className="relative py-14 md:py-20 bg-[var(--agt-navy)] overflow-hidden" data-testid="gallery-strip">
        <div className="text-center mb-10 px-6">
          <span className="eyebrow" style={{ color: "var(--agt-mustard)" }}>Instants curve</span>
          <h2 className="font-heading text-white mt-4" style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)" }}>
            <span className="font-heading-accent text-[var(--agt-mustard)] pr-4">Every body</span>is perfect
          </h2>
        </div>
        <div className="marquee-wrapper">
          <div className="marquee-track" style={{ gap: "1.5rem", animationDuration: "40s" }}>
            {galleryLoop.map((src, i) => (
              <div
                key={i}
                className="w-[240px] md:w-[320px] aspect-[3/4] flex-shrink-0 rounded-3xl overflow-hidden shadow-2xl border-2 border-[var(--agt-mustard)]/60"
              >
                <img src={src} alt="Éléonore" className="w-full h-full object-cover" loading="lazy"/>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================= BLOG PREVIEW ======================= */}
      {posts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-28">
          <div className="flex justify-between items-end mb-12 gap-4 flex-wrap">
            <div>
              <span className="eyebrow">À lire</span>
              <h2 className="font-heading mt-4 text-[var(--agt-navy)]" style={{ fontSize: "clamp(2.2rem, 5vw, 3.8rem)" }}>
                Le <span className="font-heading-accent text-[var(--agt-mustard-deep)]">blog</span>
              </h2>
            </div>
            <Link to="/blog" className="btn-ghost self-start" data-testid="home-see-blog">Tous les articles</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Link to={`/blog/${p.slug}`} className="card-soft group block" data-testid={`home-blog-${p.slug}`}>
                  {p.cover_image && (
                    <div className="overflow-hidden rounded-t-[24px]">
                      <img src={p.cover_image} alt={p.title} className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500"/>
                    </div>
                  )}
                  <div className="p-7">
                    <span className="eyebrow-navy text-[0.65rem]">Article</span>
                    <h3 className="font-heading text-2xl mt-3 leading-tight text-[var(--agt-navy)] group-hover:text-[var(--agt-mustard-deep)] transition-colors">{p.title}</h3>
                    <p className="mt-3 text-base text-[var(--agt-ink-muted)] leading-relaxed">{p.excerpt}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ======================= ELEONORE BACHER ======================= */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-28">
        <div className="rounded-[36px] overflow-hidden bg-[var(--agt-navy)] text-white relative">
          <div className="grid md:grid-cols-12 gap-0">
            <div className="md:col-span-5 relative min-h-[500px] md:min-h-[620px] overflow-hidden">
              <img
                src={ELEONORE_IMG}
                alt="Éléonore Bacher alias Beauté Ronde"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 left-6 bg-[var(--agt-mustard)] text-[var(--agt-navy)] px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest">Fondatrice</div>
            </div>

            <div className="md:col-span-7 p-8 md:p-14 flex flex-col justify-center">
              <span className="eyebrow" style={{ color: "var(--agt-mustard)" }}>Rencontrez</span>
              <h2 className="font-heading mt-5 leading-none text-white" style={{ fontSize: "clamp(2.4rem, 5vw, 4.2rem)" }}>
                Éléonore Bacher
              </h2>
              <p className="font-heading-accent text-3xl md:text-4xl text-[var(--agt-mustard)] mt-3">alias Beauté Ronde</p>
              <p className="mt-8 text-white/90 leading-relaxed text-lg">
                Passionnée de mode et militante body-positive, Éléonore Bacher est la voix derrière <strong className="text-[var(--agt-mustard)]">Beauté Ronde</strong>, référence francophone de l'inspiration mode grande taille. Blogueuse, créatrice de contenu et activiste, elle célèbre toutes les silhouettes et milite pour une mode réellement inclusive.
              </p>
              <p className="mt-4 text-white/90 leading-relaxed text-lg">
                À travers l'Annuaire Grande Taille, elle veut simplifier la vie des personnes rondes en rassemblant, en un seul endroit, les meilleures adresses shopping et associations engagées.
              </p>
              <a
                href="https://www.beauteronde.fr"
                target="_blank"
                rel="noreferrer"
                data-testid="eleonore-link"
                className="btn-primary mt-10 self-start"
              >
                Découvrir beauteronde.fr ›
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
